import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Briefcase, Search, RefreshCw, Download, Filter, X, FileText, Phone, Mail, Plus,
} from 'lucide-react';
import { BulkDeleteBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '../../components/BulkDelete';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const STATUS_META = {
  new: { label: 'New', color: 'bg-[#FF3B6B]/10 text-[#FF3B6B]' },
  reviewed: { label: 'Reviewed', color: 'bg-[#FFD166]/30 text-[#9C6B12]' },
  contacted: { label: 'Contacted', color: 'bg-[#06D6A0]/10 text-[#058B69]' },
  rejected: { label: 'Rejected', color: 'bg-[#6B6B7A]/15 text-[#6B6B7A]' },
  hired: { label: 'Hired', color: 'bg-[#8B5CF6]/10 text-[#6D3FC9]' },
};

const statusBadge = (s) => {
  const m = STATUS_META[s] || { label: s, color: 'bg-[#E8E5DF] text-[#1E1E24]' };
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${m.color}`}>{m.label}</span>;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const fmtPhone = (raw) => {
  const d = (raw || '').replace(/\D+/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
};

export const CrmJobApplications = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ job_title: 'all', status: 'all', applied_via: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState(null);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ job_title: 'Staff', full_name: '', email: '', phone: '', availability: '', experience: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const fetchList = async (resetPage = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.job_title !== 'all') params.set('job_title', filters.job_title);
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.applied_via !== 'all') params.set('applied_via', filters.applied_via);
      if (filters.search) params.set('search', filters.search);
      const skip = resetPage ? 0 : page * PAGE_SIZE;
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const res = await axios.get(`${API_URL}/api/admin/job-applications?${params.toString()}`, { withCredentials: true });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      if (resetPage) setPage(0);
    } catch (e) {
      toast.error('Failed to load applications');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { fetchList(true); /* eslint-disable-next-line */ }, [filters.job_title, filters.status, filters.applied_via, filters.search]);

  const openDetail = (row) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditNotes(row.notes || '');
  };

  const saveEdits = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/job-applications/${selected.application_id}`,
        { status: editStatus, notes: editNotes }, { withCredentials: true });
      toast.success('Application updated');
      setSelected(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const downloadResume = (app) => {
    if (!app.resume) { toast.info('No resume on file'); return; }
    // Use an anchor with credentials via cookie — browser will include them
    const a = document.createElement('a');
    a.href = `${API_URL}/api/admin/job-applications/${app.application_id}/resume`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  };

  const exportCsv = () => {
    window.location.href = `${API_URL}/api/admin/job-applications/export.csv`;
  };

  const resetFilters = () => {
    setFilters({ job_title: 'all', status: 'all', applied_via: 'all', search: '' });
    setSearchInput('');
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const hasActiveFilters = filters.job_title !== 'all' || filters.status !== 'all' || filters.applied_via !== 'all' || filters.search;

  const addPhoneApp = async () => {
    if (!addForm.full_name.trim() || !(addForm.phone || '').replace(/\D+/g, '').length) {
      toast.error('Name and phone required'); return;
    }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/job-applications`, addForm, { withCredentials: true });
      toast.success('Phone application logged');
      setAddOpen(false);
      setAddForm({ job_title: 'Staff', full_name: '', email: '', phone: '', availability: '', experience: '', notes: '' });
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setAdding(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-6 h-6 text-[#FF3B6B]" />
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Job Applications
            </h1>
          </div>
          <p className="text-[#6B6B7A] text-sm">
            Applicants for the Careers page. {total > 0 && <span className="font-medium">{total} records</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchList()} className="rounded-full border-[#E8E5DF]" data-testid="jobapps-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} className="rounded-full border-[#E8E5DF]" data-testid="jobapps-export">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => setAddOpen(true)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="jobapps-add-phone">
            <Plus className="w-4 h-4 mr-2" /> Log phone application
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-[#6B6B7A] text-sm">
          <Filter className="w-4 h-4" /> Filters
          {hasActiveFilters && (
            <button onClick={resetFilters} className="ml-auto text-xs flex items-center gap-1 text-[#FF3B6B] hover:underline" data-testid="jobapps-clear-filters">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={filters.job_title} onValueChange={(v) => setFilters((f) => ({ ...f, job_title: v }))}>
            <SelectTrigger data-testid="jobapps-filter-job" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="Party Host">Party Host</SelectItem>
              <SelectItem value="Staff">Staff</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="jobapps-filter-status" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.applied_via} onValueChange={(v) => setFilters((f) => ({ ...f, applied_via: v }))}>
            <SelectTrigger data-testid="jobapps-filter-via" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Online + Phone</SelectItem>
              <SelectItem value="online">Online only</SelectItem>
              <SelectItem value="phone">Phone only</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
            <Input
              placeholder="Search name / email / phone / experience…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setFilters((f) => ({ ...f, search: searchInput.trim() })); }}
              className="pl-9 rounded-xl border-[#E8E5DF]"
              data-testid="jobapps-filter-search"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <BulkSelectAllCheckbox ids={entries.map((a) => a.application_id)} selected={bulkSelected} setSelected={setBulkSelected} testid="jobapps-select-all" />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Role</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="w-24">Channel</TableHead>
              <TableHead className="w-36">Received</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-[#6B6B7A]" data-testid="jobapps-empty">
                No applications yet. They'll appear here as they come in.
              </TableCell></TableRow>
            ) : entries.map((a) => (
              <TableRow key={a.application_id} className="hover:bg-[#FFFDF8]" data-testid={`jobapp-row-${a.application_id}`}>
                <TableCell>
                  <BulkSelectCheckbox id={a.application_id} selected={bulkSelected} setSelected={setBulkSelected} testid={`jobapp-checkbox-${a.application_id}`} />
                </TableCell>
                <TableCell className="font-medium text-[#1E1E24]">
                  {a.full_name}
                  {a.resume && <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#06D6A0]/10 text-[#058B69]"><FileText className="w-3 h-3" /> Resume</span>}
                </TableCell>
                <TableCell className="text-sm text-[#1E1E24]">{a.job_title}</TableCell>
                <TableCell className="text-xs text-[#6B6B7A]">
                  {a.email && <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email}</p>}
                  <p className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {fmtPhone(a.phone)}</p>
                </TableCell>
                <TableCell className="text-xs capitalize text-[#6B6B7A]">{a.applied_via}</TableCell>
                <TableCell className="text-xs text-[#6B6B7A] whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                <TableCell>{statusBadge(a.status)}</TableCell>
                <TableCell>
                  <Button size="sm" onClick={() => openDetail(a)} className="rounded-full bg-[#1E1E24] hover:bg-[#2E2E34] text-white" data-testid={`jobapp-open-${a.application_id}`}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-[#6B6B7A]">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="jobapps-prev-page">Previous</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)} data-testid="jobapps-next-page">Next</Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="jobapp-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>{selected?.full_name}</DialogTitle>
            <DialogDescription>
              {selected?.job_title} · applied {selected?.applied_via} · {fmtDate(selected?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <span className="text-[#6B6B7A]">Email</span>
                <span className="text-[#1E1E24]">{selected.email || '—'}</span>
                <span className="text-[#6B6B7A]">Phone</span>
                <span className="text-[#1E1E24]">{fmtPhone(selected.phone)}</span>
                {selected.availability && (<>
                  <span className="text-[#6B6B7A]">Availability</span>
                  <span className="text-[#1E1E24] whitespace-pre-wrap">{selected.availability}</span>
                </>)}
              </div>
              {selected.experience && (
                <div>
                  <p className="text-xs font-semibold text-[#6B6B7A] uppercase tracking-wide mb-1">Experience</p>
                  <p className="text-[#1E1E24] whitespace-pre-wrap bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">{selected.experience}</p>
                </div>
              )}
              {selected.resume && (
                <Button variant="outline" onClick={() => downloadResume(selected)} className="w-full rounded-full border-[#E8E5DF]" data-testid="jobapp-resume-download">
                  <Download className="w-4 h-4 mr-2" /> Download {selected.resume.filename}
                </Button>
              )}
              <div>
                <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="rounded-xl border-[#E8E5DF]" data-testid="jobapp-edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#1E1E24] mb-1 block">Internal notes</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="Interview prep, references, next steps…"
                  className="rounded-xl border-[#E8E5DF]"
                  data-testid="jobapp-edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full" data-testid="jobapp-cancel">Cancel</Button>
            <Button onClick={saveEdits} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="jobapp-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add phone-application dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="jobapp-add-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Log a phone application</DialogTitle>
            <DialogDescription>Use this when a candidate calls the store to apply.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Role</Label>
              <Select value={addForm.job_title} onValueChange={(v) => setAddForm((f) => ({ ...f, job_title: v }))}>
                <SelectTrigger className="rounded-xl border-[#E8E5DF] mt-1" data-testid="jobapp-add-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Party Host">Party Host</SelectItem>
                  <SelectItem value="Staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Full name</Label>
              <Input value={addForm.full_name} onChange={(e) => setAddForm((f) => ({ ...f, full_name: e.target.value }))} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="jobapp-add-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="jobapp-add-phone" />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="jobapp-add-email" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={addForm.notes} onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-xl border-[#E8E5DF] mt-1" data-testid="jobapp-add-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={addPhoneApp} disabled={adding} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="jobapp-add-save">
              {adding ? 'Saving…' : 'Save application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkDeleteBar
        section="job_applications"
        selectedIds={bulkSelected}
        onClearSelection={() => setBulkSelected([])}
        onDone={() => fetchList()}
        labelSingular="application"
        dataTestPrefix="jobapps-bulk"
      />
    </div>
  );
};

export default CrmJobApplications;
